import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ThemeMode } from '../types';

/** What the user picked. `system` follows the OS setting and keeps following it as it changes. */
export type ThemeModePreference = ThemeMode | 'system';

export interface ThemeModeContextValue {
  /** The user's choice, which may be `system`. */
  preference: ThemeModePreference;
  /** What that resolves to right now — always a concrete mode. */
  mode: ThemeMode;
  setPreference: (preference: ThemeModePreference) => void;
}

export const STORAGE_KEY = 'enmon.theme-mode';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemeModePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const subscribeToSystem = (onChange: () => void) => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined;
  }
  const query = window.matchMedia(MEDIA_QUERY);
  query.addEventListener('change', onChange);

  return () => query.removeEventListener('change', onChange);
};

const getSystemMode = (): ThemeMode =>
  typeof window !== 'undefined' && window.matchMedia?.(MEDIA_QUERY).matches ? 'dark' : 'light';

/** The OS colour scheme, kept live. `useSyncExternalStore` is what keeps it in step with React 18. */
const getLightMode = (): ThemeMode => 'light';
const noSubscription = () => () => undefined;

const useSystemThemeMode = (enabled: boolean): ThemeMode =>
  useSyncExternalStore(
    enabled ? subscribeToSystem : noSubscription,
    enabled ? getSystemMode : getLightMode,
    getLightMode,
  );

const DEFAULT_PREFERENCE: ThemeModePreference = 'light';

const readStoredPreference = (): ThemeModePreference => {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCE;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return isPreference(stored) ? stored : DEFAULT_PREFERENCE;
  } catch {
    // Private mode / blocked storage: fall back rather than take the app down over a preference.
    return DEFAULT_PREFERENCE;
  }
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/**
 * Holds the light/dark preference, persists it, and resolves `system` against the OS.
 *
 * Mount it above whatever builds the antd theme — both apps do that inside their own
 * `AppAntConfigProvider`, so no app entry point needs to change.
 */
export interface ThemeModeProviderProps extends PropsWithChildren {
  /**
   * false means light (default for now).
   */
  enabled?: boolean;
}

export const ThemeModeProvider: FC<ThemeModeProviderProps> = ({ children, enabled = true }) => {
  const [preference, setStoredPreference] = useState<ThemeModePreference>(() =>
    enabled ? readStoredPreference() : DEFAULT_PREFERENCE,
  );
  const systemMode = useSystemThemeMode(enabled);

  const setPreference = useCallback((next: ThemeModePreference) => {
    setStoredPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference just will not survive a reload; not worth throwing over.
    }
  }, []);

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      preference: enabled ? preference : 'light',
      mode: enabled ? (preference === 'system' ? systemMode : preference) : 'light',
      setPreference,
    }),
    [enabled, preference, systemMode, setPreference],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

/** Throws outside a {@link ThemeModeProvider} — a switcher that silently does nothing is worse. */
export const useThemeMode = (): ThemeModeContextValue => {
  const value = useContext(ThemeModeContext);

  if (!value) {
    throw new Error('useThemeMode must be used inside a <ThemeModeProvider>');
  }

  return value;
};

/** For components that should work with or without the provider. */
export const useOptionalThemeMode = (): ThemeModeContextValue | null => useContext(ThemeModeContext);
